import { useEffect, useRef, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Progress } from "@/components/ui/progress";
import { Textarea } from "@/components/ui/textarea";
import { Checkbox } from "@/components/ui/checkbox";
import { AlertDialog, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";
import { ExternalLink } from "lucide-react";

const NEI = { value: null, reason: "not_enough_information" };
const ROTTA_ID = "itsmatch-rotta-4.0.0";

export default function SectionBPage() {
  const navigate = useNavigate();
  const [session, setSession] = useState(null);
  const [responses, setResponses] = useState({});
  const [openAnswers, setOpenAnswers] = useState({ O1: "", O2: {}, O3: { text: "", exploring: false }, O4: { text: "", no_example: false } });
  const [answers, setAnswers] = useState({});
  const [orAnswers, setOrAnswers] = useState({});
  const [remaining, setRemaining] = useState(null);
  const [busy, setBusy] = useState(false);
  const [omissionItems, setOmissionItems] = useState([]);
  const [showOmissions, setShowOmissions] = useState(false);
  const submittedRef = useRef(false);
  const loadedRef = useRef(false);
  const videoRef = useRef(null);
  const streamRef = useRef(null);
  const submitRef = useRef(() => {});

  useEffect(() => {
    const sid = localStorage.getItem("its_test_session");
    if (!sid) { navigate("/dashboard"); return; }
    api.get(`/tests/${sid}`).then(({ data }) => {
      if (data.status === "section_a_active") { navigate("/test"); return; }
      if (["completed", "screened_out", "terminated"].includes(data.status)) { navigate("/test/completamento"); return; }
      if (!data.section_b_started_at) { navigate("/test/parte-b"); return; }
      if (!data.questionnaire) { navigate("/dashboard"); return; }
      const draft = data.section_b_draft;
      if (draft) {
        if (data.questionnaire.instrument_id === ROTTA_ID) {
          setAnswers(draft.responses || {});
          setOrAnswers(draft.open_answers || {});
        } else {
          setResponses(draft.responses || {});
          setOpenAnswers((prev) => ({ ...prev, ...(draft.open_answers || {}) }));
        }
      }
      loadedRef.current = true;
      setSession(data);
    }).catch(() => navigate("/dashboard"));
  }, [navigate]);

  // Webcam attiva anche nella sezione B: snapshot ogni 60s per tutta la sessione
  useEffect(() => {
    if (!session) return;
    let cancelled = false;
    navigator.mediaDevices?.getUserMedia({ video: true })
      .then((stream) => {
        if (cancelled) { stream.getTracks().forEach((t) => t.stop()); return; }
        streamRef.current = stream;
        if (videoRef.current) videoRef.current.srcObject = stream;
        api.post(`/tests/${session.id}/event`, { event: "CAMERA_PERMISSION_GRANTED", detail: "sezione B" }).catch(() => {});
      })
      .catch(() => {
        api.post(`/tests/${session.id}/event`, { event: "CAMERA_PERMISSION_DENIED", detail: "sezione B" }).catch(() => {});
      });
    const snap = setInterval(() => {
      const video = videoRef.current;
      if (!video || !video.videoWidth) return;
      const canvas = document.createElement("canvas");
      canvas.width = 640;
      canvas.height = Math.round(640 * (video.videoHeight / video.videoWidth)) || 480;
      canvas.getContext("2d").drawImage(video, 0, 0, canvas.width, canvas.height);
      const image = canvas.toDataURL("image/jpeg", 0.6);
      api.post(`/tests/${session.id}/snapshot`, { image }).catch(() => {});
    }, 60000);
    return () => {
      cancelled = true;
      clearInterval(snap);
      streamRef.current?.getTracks().forEach((t) => t.stop());
    };
  }, [session]);

  const isRotta = session?.questionnaire?.instrument_id === ROTTA_ID;

  // Bozza automatica lato server (rientro da disconnessione senza perdere le risposte)
  useEffect(() => {
    if (!session || !loadedRef.current) return;
    const t = setTimeout(() => {
      const payload = isRotta
        ? { responses: answers, open_answers: orAnswers }
        : { responses, open_answers: openAnswers };
      api.post(`/tests/${session.id}/section-b-draft`, payload).catch(() => {});
    }, 1500);
    return () => clearTimeout(t);
  }, [responses, openAnswers, answers, orAnswers, session, isRotta]);

  const exitFullscreen = () => {
    if (!document.fullscreenElement || !document.exitFullscreen) return;
    document.exitFullscreen()
      .then(() => {
        if (session?.id) api.post(`/tests/${session.id}/event`, { event: "FULLSCREEN_EXIT_COMPLETED", detail: "test completato" }).catch(() => {});
      })
      .catch(() => {});
  };

  const doSubmit = async (confirmed) => {
    if (submittedRef.current || !session) return;
    submittedRef.current = true;
    try {
      const payload = isRotta
        ? { answers, or_answers: orAnswers, confirmed_omissions: confirmed }
        : { responses, open_answers: openAnswers, confirmed_omissions: confirmed };
      await api.post(`/tests/${session.id}/submit-section-b`, payload);
      exitFullscreen();
      navigate("/test/completamento");
    } catch (e) {
      submittedRef.current = false;
      setBusy(false);
      const detail = e?.response?.data?.detail;
      if (detail && typeof detail === "object" && detail.code === "omissions") {
        setOmissionItems(detail.items || []);
        setShowOmissions(true);
      } else {
        toast.error(formatApiError(e));
      }
    }
  };
  submitRef.current = doSubmit;

  useEffect(() => {
    if (!session?.section_b_deadline) return;
    const tick = () => {
      const left = Math.max(0, Math.floor((new Date(session.section_b_deadline) - Date.now()) / 1000));
      setRemaining(left);
      // Auto-consegna allo scadere: le mancanti restano senza risposta, nessun valore imputato
      if (left <= 0) submitRef.current(true);
    };
    tick();
    const t = setInterval(tick, 1000);
    return () => clearInterval(t);
  }, [session]);

  if (!session) {
    return (
      <div className="min-h-screen bg-slate-900 flex items-center justify-center" data-testid="section-b-loading">
        <div className="h-8 w-8 animate-spin rounded-full border-4 border-slate-700 border-t-sky-400" />
      </div>
    );
  }

  const q = session.questionnaire;
  const allCodes = isRotta
    ? [...q.items.map((it) => it.id), ...q.or_questions.map((it) => it.id)]
    : [...q.areas.flatMap((a) => a.items.map((it) => it.code)), ...q.qualitative_items.map((it) => it.code)];
  const answeredCount = isRotta
    ? q.items.filter((it) => answers[it.id]).length + q.or_questions.filter((it) => orAnswers[it.id]).length
    : allCodes.filter((c) => responses[c] !== undefined).length;
  const unanswered = isRotta
    ? allCodes.filter((c) => (q.or_questions.some((o) => o.id === c) ? !orAnswers[c] : !answers[c]))
    : allCodes.filter((c) => responses[c] === undefined);
  const mm = String(Math.floor((remaining ?? 0) / 60)).padStart(2, "0");
  const ss = String((remaining ?? 0) % 60).padStart(2, "0");
  const prefs = [...(session.course_preferences || [])].sort((a, b) => a.rank - b.rank);

  const handleSubmitClick = () => {
    if (unanswered.length > 0) {
      setOmissionItems(unanswered);
      setShowOmissions(true);
      return;
    }
    setBusy(true);
    doSubmit(true);
  };

  const setOpen = (patch) => setOpenAnswers((prev) => ({ ...prev, ...patch }));

  const renderItem = (it, numbered) => {
    const sel = responses[it.code];
    const isNei = sel?.reason === "not_enough_information";
    return (
      <Card key={it.code} data-testid={`likert-item-${it.code}`} className="bg-slate-800/80 border-slate-700 text-slate-100">
        <CardContent className="p-5">
          <p className="text-sm sm:text-base font-medium text-slate-100">
            <span className="font-mono2 text-sky-400 mr-2">{String(numbered).padStart(2, "0")}</span>
            {it.text}
          </p>
          <RadioGroup className="mt-4 grid grid-cols-2 sm:grid-cols-5 gap-2"
            value={sel?.value ? String(sel.value) : ""}
            onValueChange={(v) => setResponses({ ...responses, [it.code]: { value: parseInt(v, 10), reason: null } })}>
            {q.scale.map((l) => (
              <label key={l.value} data-testid={`likert-option-${it.code}-${l.value}`}
                className={`flex flex-col items-center gap-1.5 rounded-lg border p-2.5 cursor-pointer transition-colors text-center ${
                  sel?.value === l.value ? "border-sky-400 bg-sky-400/10" : "border-slate-600 hover:border-slate-500"
                }`}>
                <RadioGroupItem value={String(l.value)} />
                <span className="text-xs text-slate-300">{l.value} · {l.label}</span>
              </label>
            ))}
          </RadioGroup>
          <button type="button" data-testid={`likert-nei-${it.code}`}
            onClick={() => setResponses({ ...responses, [it.code]: { ...NEI } })}
            className={`mt-2 text-xs rounded-md border px-2.5 py-1.5 transition-colors ${
              isNei ? "border-sky-400 bg-sky-400/10 text-sky-300" : "border-slate-600 text-slate-400 hover:border-slate-500"
            }`}>
            Non ho elementi per rispondere
          </button>
        </CardContent>
      </Card>
    );
  };

  let counter = 0;

  return (
    <div className="min-h-screen bg-[#0B1120] text-slate-100" data-testid="section-b-page">
      <div className="sticky top-0 z-30 bg-slate-900/95 backdrop-blur-md border-b border-slate-700">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between">
          <div>
            <div className="proctor-badge text-sky-400">SEZIONE B · {isRotta ? "LA TUA SCELTA ITS" : "QUESTIONARIO ORIENTATIVO"}</div>
            <div className="text-xs text-slate-400">Risposte: {answeredCount}/{allCodes.length}</div>
          </div>
          <div data-testid="section-b-timer" className={`font-mono2 text-xl font-semibold ${remaining !== null && remaining < 180 ? "text-red-400" : "text-slate-100"}`}>
            {mm}:{ss}
          </div>
        </div>
        <Progress value={allCodes.length ? (answeredCount / allCodes.length) * 100 : 0} className="h-1 rounded-none" />
      </div>

      <div data-testid="proctor-webcam-pip" className="fixed top-20 right-4 z-40 w-32 sm:w-44 rounded-lg overflow-hidden border border-slate-600 bg-slate-800 shadow-2xl">
        <div className="flex items-center gap-1.5 px-2 py-1 bg-slate-900/95">
          <span className="rec-dot h-2 w-2 rounded-full bg-red-500" />
          <span className="proctor-badge text-red-400 text-[10px]">PROCTORING ATTIVO</span>
        </div>
        <video ref={videoRef} autoPlay muted playsInline className="w-full h-28 object-cover" />
      </div>

      <div className="max-w-3xl mx-auto px-4 sm:px-6 pt-24 pb-10 sm:py-10">
        {isRotta ? (
          <>
            <Card className="bg-slate-800/60 border-slate-700 text-slate-100 mb-8">
              <CardContent className="p-5">
                <h2 className="text-lg font-semibold text-slate-100 mb-2" data-testid="rotta-intro-title">{q.title}</h2>
                <p className="text-sm text-slate-300 leading-relaxed" data-testid="questionnaire-instructions">{q.intro_text}</p>
                <p className="text-xs text-slate-400 leading-relaxed mt-3" data-testid="rotta-disclosure">{q.disclosure_text}</p>
                <p className="text-xs text-sky-300 mt-3" data-testid="rotta-time-estimate">{q.time_estimate}</p>
              </CardContent>
            </Card>

            <div className="space-y-4">
              {q.items.map((it, idx) => (
                <Card key={it.id} data-testid={`rotta-item-${it.id}`} className="bg-slate-800/80 border-slate-700 text-slate-100">
                  <CardContent className="p-5">
                    <p className="text-sm sm:text-base font-medium text-slate-100 leading-relaxed">
                      <span className="font-mono2 text-sky-400 mr-2">{String(idx + 1).padStart(2, "0")}</span>
                      {it.stem}
                    </p>
                    <RadioGroup className="mt-4 space-y-2" value={answers[it.id] || ""}
                      onValueChange={(v) => setAnswers({ ...answers, [it.id]: v })}>
                      {it.options.map((o) => (
                        <label key={o.id} data-testid={`rotta-option-${it.id}-${o.id}`}
                          className={`flex items-start gap-3 rounded-lg border p-3 cursor-pointer transition-colors ${
                            answers[it.id] === o.id ? "border-sky-400 bg-sky-400/10" : "border-slate-600 hover:border-slate-500"
                          }`}>
                          <RadioGroupItem value={o.id} className="mt-0.5" />
                          <span className="text-sm text-slate-200 leading-relaxed">{o.text}</span>
                        </label>
                      ))}
                    </RadioGroup>
                  </CardContent>
                </Card>
              ))}
            </div>

            <div className="mt-8 mb-4">
              <p className="proctor-badge text-sky-400">CONDIZIONI ORGANIZZATIVE</p>
              <p className="text-xs text-slate-400 mt-1">Tre domande chiuse sulla fattibilità organizzativa, senza dettagli personali.</p>
            </div>
            <div className="space-y-4">
              {q.or_questions.map((it) => (
                <Card key={it.id} data-testid={`rotta-or-${it.id}`} className="bg-slate-800/80 border-slate-700 text-slate-100">
                  <CardContent className="p-5">
                    <p className="text-sm font-medium text-slate-100 leading-relaxed">{it.stem}</p>
                    <RadioGroup className="mt-3 space-y-1.5" value={orAnswers[it.id] || ""}
                      onValueChange={(v) => setOrAnswers({ ...orAnswers, [it.id]: v })}>
                      {it.options.map((o) => (
                        <label key={o.id} data-testid={`rotta-or-option-${it.id}-${o.id}`}
                          className={`flex items-center gap-3 rounded-lg border px-3 py-2 cursor-pointer transition-colors ${
                            orAnswers[it.id] === o.id ? "border-sky-400 bg-sky-400/10" : "border-slate-600 hover:border-slate-500"
                          }`}>
                          <RadioGroupItem value={o.id} />
                          <span className="text-xs text-slate-300">{o.label}</span>
                        </label>
                      ))}
                    </RadioGroup>
                  </CardContent>
                </Card>
              ))}
            </div>
          </>
        ) : (
          <>
            <Card className="bg-slate-800/60 border-slate-700 text-slate-100 mb-8">
              <CardContent className="p-5">
                <p className="text-sm text-slate-300 leading-relaxed" data-testid="questionnaire-instructions">{q.instructions}</p>
              </CardContent>
            </Card>

            {q.areas.map((area) => (
              <div key={area.id} className="mb-8">
                <div className="mb-3">
                  <p className="proctor-badge text-sky-400" data-testid={`area-header-${area.id}`}>AREA {area.id} · {area.label}</p>
                  <p className="text-xs text-slate-400 mt-1">{area.description}</p>
                </div>
                <div className="space-y-4">
                  {area.items.map((it) => renderItem(it, ++counter))}
                </div>
              </div>
            ))}

            <div className="mb-8">
              <p className="proctor-badge text-sky-400 mb-3">AFFERMAZIONI AGGIUNTIVE</p>
              <div className="space-y-4">
                {q.qualitative_items.map((it) => renderItem(it, ++counter))}
              </div>
            </div>

            <div className="mb-4">
              <p className="proctor-badge text-sky-400">APPROFONDIMENTI SULLE PREFERENZE</p>
              <p className="text-xs text-slate-400 mt-1">
                Domande aperte facoltative, senza punteggio: aiutano la commissione a preparare il colloquio.
              </p>
              <p className="text-xs text-amber-300/90 mt-2" data-testid="open-fields-warning">{q.open_fields_warning}</p>
            </div>

            <div className="space-y-4">
              <Card className="bg-slate-800/80 border-slate-700 text-slate-100">
                <CardContent className="p-5">
                  <p className="text-sm font-medium" data-testid="open-o1-label">O1 · {q.open_questions.find((x) => x.id === "O1")?.text}</p>
                  <Textarea data-testid="open-o1" rows={3} maxLength={500} className="mt-3 bg-slate-900/60 border-slate-600 text-slate-100 text-sm"
                    value={openAnswers.O1} onChange={(e) => setOpen({ O1: e.target.value })} />
                  <p className="text-[10px] text-slate-500 mt-1 text-right">{openAnswers.O1.length}/500</p>
                </CardContent>
              </Card>

              <Card className="bg-slate-800/80 border-slate-700 text-slate-100">
                <CardContent className="p-5">
                  <p className="text-sm font-medium" data-testid="open-o2-label">O2 · {q.open_questions.find((x) => x.id === "O2")?.text}</p>
                  <div className="mt-3 space-y-4">
                    {prefs.map((p) => {
                      const entry = openAnswers.O2?.[String(p.rank)] || {};
                      return (
                        <div key={p.rank} className="border border-slate-700 rounded-lg p-3" data-testid={`open-o2-row-${p.rank}`}>
                          <div className="flex flex-wrap items-center justify-between gap-2">
                            <p className="text-xs font-semibold text-slate-200">{p.rank}ª preferenza · {p.course_name}</p>
                            <a href={`/candidati/${session.tenant_slug}`} target="_blank" rel="noreferrer"
                              data-testid={`open-o2-link-${p.rank}`}
                              className="text-[11px] text-sky-400 hover:underline inline-flex items-center gap-1">
                              Riepilogo del corso <ExternalLink className="h-3 w-3" />
                            </a>
                          </div>
                          <Textarea data-testid={`open-o2-${p.rank}`} rows={2} maxLength={250}
                            disabled={!!entry.unknown_course}
                            className="mt-2 bg-slate-900/60 border-slate-600 text-slate-100 text-sm disabled:opacity-40"
                            value={entry.text || ""}
                            onChange={(e) => setOpen({ O2: { ...openAnswers.O2, [String(p.rank)]: { ...entry, text: e.target.value } } })} />
                          <label className="mt-2 flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                            <Checkbox data-testid={`open-o2-unknown-${p.rank}`} checked={!!entry.unknown_course}
                              onCheckedChange={(c) => setOpen({ O2: { ...openAnswers.O2, [String(p.rank)]: { ...entry, unknown_course: !!c } } })} />
                            Non conosco ancora abbastanza questo corso
                          </label>
                        </div>
                      );
                    })}
                    {prefs.length === 0 && (
                      <p className="text-xs text-slate-400">Corso indicato nella candidatura: {session.course_name || "—"}</p>
                    )}
                  </div>
                </CardContent>
              </Card>

              <Card className="bg-slate-800/80 border-slate-700 text-slate-100">
                <CardContent className="p-5">
                  <p className="text-sm font-medium" data-testid="open-o3-label">O3 · {q.open_questions.find((x) => x.id === "O3")?.text}</p>
                  <Textarea data-testid="open-o3" rows={3} maxLength={500} disabled={!!openAnswers.O3?.exploring}
                    className="mt-3 bg-slate-900/60 border-slate-600 text-slate-100 text-sm disabled:opacity-40"
                    value={openAnswers.O3?.text || ""}
                    onChange={(e) => setOpen({ O3: { ...openAnswers.O3, text: e.target.value } })} />
                  <label className="mt-2 flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                    <Checkbox data-testid="open-o3-exploring" checked={!!openAnswers.O3?.exploring}
                      onCheckedChange={(c) => setOpen({ O3: { ...openAnswers.O3, exploring: !!c } })} />
                    La mia scelta è ancora da chiarire
                  </label>
                </CardContent>
              </Card>

              <Card className="bg-slate-800/80 border-slate-700 text-slate-100">
                <CardContent className="p-5">
                  <p className="text-sm font-medium" data-testid="open-o4-label">O4 · {q.open_questions.find((x) => x.id === "O4")?.text}</p>
                  <Textarea data-testid="open-o4" rows={4} maxLength={700} disabled={!!openAnswers.O4?.no_example}
                    className="mt-3 bg-slate-900/60 border-slate-600 text-slate-100 text-sm disabled:opacity-40"
                    value={openAnswers.O4?.text || ""}
                    onChange={(e) => setOpen({ O4: { ...openAnswers.O4, text: e.target.value } })} />
                  <label className="mt-2 flex items-center gap-2 text-xs text-slate-400 cursor-pointer">
                    <Checkbox data-testid="open-o4-no-example" checked={!!openAnswers.O4?.no_example}
                      onCheckedChange={(c) => setOpen({ O4: { ...openAnswers.O4, no_example: !!c } })} />
                    Non ho un esempio da riportare
                  </label>
                </CardContent>
              </Card>
            </div>
          </>
        )}

        <Button data-testid="section-b-submit-button" size="lg" disabled={busy}
          className="w-full mt-8 bg-[#E05A47] hover:bg-[#c94836] text-base font-semibold"
          onClick={handleSubmitClick}>
          {busy ? "Invio in corso..." : "Invia risposte e concludi test"}
        </Button>
      </div>

      <AlertDialog open={showOmissions} onOpenChange={setShowOmissions}>
        <AlertDialogContent data-testid="omissions-dialog">
          <AlertDialogHeader>
            <AlertDialogTitle>Riepilogo omissioni</AlertDialogTitle>
            <AlertDialogDescription asChild>
              <div className="text-sm text-slate-600">
                <p>Non hai risposto a {omissionItems.length} {isRotta ? "domande" : "affermazioni"} ({omissionItems.join(", ")}).
                  Puoi rivederle ora oppure inviare comunque: nessuna omissione comporta un'esclusione automatica.</p>
              </div>
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="omissions-back-button">Torna a completare</AlertDialogCancel>
            <Button data-testid="omissions-confirm-button" className="bg-[#E05A47] hover:bg-[#c94836]"
              onClick={() => { setShowOmissions(false); setBusy(true); doSubmit(true); }}>
              Conferma e invia comunque
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
